import { httpClient } from '../lib/httpClient';
import { queryClient } from '../lib/queryClient';
import type { KnowledgeGraphEntry, KnowledgeGraphContradiction } from '../types/complianceWorkflow';

export async function getClientKnowledge(clientId: string, domain?: string) {
  const response = await httpClient.get<{ entries: KnowledgeGraphEntry[] } | KnowledgeGraphEntry[]>(
    `/api/knowledge-graph/${clientId}`,
    { params: domain ? { domain } : undefined }
  );
  // API returns { client_id, total, entries } — unwrap to just the array
  if (response && !Array.isArray(response) && 'entries' in response) {
    return response.entries ?? [];
  }
  return Array.isArray(response) ? response : [];
}

// ── Derivation chain (TASK-683) ──────────────────────────────────────────
// The claims behind a widget's figures, each with the sentence it came from. Only claims
// with a stored excerpt come back — an empty list means "no derivation recorded", which is
// an honest answer and never to be filled in with prose.

/** Where a claim sits in its source. Shared by the derivation chain and the DNA items. */
export interface ProvenanceLocator {
  page?: number;
  line?: number;
  char_span?: number[];
  transcript_line?: number;
  /** TASK-682 review: CSV/Excel claims cite the cell they came from. */
  sheet?: string;
  row?: number;
  column?: string;
}

export interface DerivationStep {
  entry_id: string;
  domain: string;
  field_path: string;
  value: unknown;
  confidence: number | null;
  excerpt: string;
  locator: ProvenanceLocator | null;
  observation_count: number | null;
  origin: string | null;
  source_document_id: string | null;
  source_document_name: string | null;
  /** TASK-683 review: resolved from the excerpt for meeting claims, so the quote is openable. */
  source_session_id: string | null;
  source_utterance_seq: number | null;
  verified: boolean;
  recorded_at: string | null;
}

export interface DerivationResponse {
  client_id: string;
  component: string | null;
  steps: DerivationStep[];
}

export async function getDerivation(clientId: string, component?: string, limit = 6) {
  return httpClient.get<DerivationResponse>(`/api/knowledge-graph/${clientId}/derivation`, {
    params: { ...(component ? { component } : {}), limit },
  });
}

// ── Cited meeting openers (TASK-689) ─────────────────────────────────────
// What to OPEN the meeting with, in the client's own words. Built server-side by
// `shared/skills/generate_agenda.build_openers` — a claim with no stored excerpt never
// becomes an opener, so an empty list means "nothing citable yet", not "request failed".

export interface MeetingOpener {
  /** "You told me you care about" — the lead-in, per DNA category. */
  lead: string;
  /** What it is about, in the graph's words ("Supply-chain due diligence names"). */
  subject: string | null;
  excerpt: string;
  locator: ProvenanceLocator | null;
  observation_count: number | null;
  category: string;
  claim_id: string | null;
}

export interface OpenersResponse {
  client_id: string;
  openers: MeetingOpener[];
  openers_available: boolean;
  uncited_claims: number;
}

export async function getOpeners(clientId: string, limit = 3) {
  return httpClient.get<OpenersResponse>(`/api/knowledge-graph/${clientId}/openers`, {
    params: { limit },
  });
}

export async function getContradictions(clientId: string) {
  return httpClient.get<KnowledgeGraphContradiction[]>(`/api/knowledge-graph/${clientId}/contradictions`);
}

// ── Client DNA (TASK-474) ────────────────────────────────────────────────
// DNA is stored in the KG under the `values` domain; this endpoint assembles it.

export interface DnaSource {
  field_path?: string;
  source_document_id?: string | null;
  meeting_id?: string;
}

export interface DnaItem {
  entry_id: string;
  text: string;
  tag: string | null;
  confidence: number | null;
  sources: DnaSource[];
  verified: boolean;
  // TASK-576 Stage C: life_event items carry structured fields (absent on other
  // DNA categories). Powers the first-class Life-Events widget + action loop.
  event_id?: string;
  event_type?: string;
  event_date?: string | null;
  financial_impact?: string | null;
  quote?: string;
  // TASK-682's citation, now carried by /dna (review fix). Absent on any claim recorded
  // before TASK-682 — an absent excerpt renders as nothing, never as a synthesised quote.
  source_excerpt?: string | null;
  source_locator?: ProvenanceLocator | null;
  observation_count?: number | null;
  origin?: string | null;
}

export interface DnaResponse {
  client_id: string;
  mandate: string | null;
  values: DnaItem[];
  exclusions: DnaItem[];
  tilts: DnaItem[];
  promises: DnaItem[];
  life_events: DnaItem[];
  temperament: string | null;
  business_context: string | null;
  family_context: string | null;
}

export async function getClientDna(clientId: string) {
  return httpClient.get<DnaResponse>(`/api/knowledge-graph/${clientId}/dna`);
}

export type KGField = { field_path: string; value: unknown; confidence?: number; source?: string };

export async function upsertFields(clientId: string, fields: KGField[]) {
  // The route returns `entries` with each upserted row id (already on the wire);
  // surfacing it lets callers verify the rows they just wrote (see approveFields).
  return httpClient.post<{
    upserted: number;
    errors: unknown[];
    entries: Array<{ id: string; field_path: string }>;
  }>(`/api/knowledge-graph/${clientId}/upsert`, { fields });
}

/**
 * Approve = upsert + mark verified. An explicit RM Apply click is the human gate
 * (Laws L1/L2), so the written rows are immediately verified (verified=true =
 * RM-approved). Returns the number of rows verified.
 */
export async function approveFields(clientId: string, fields: KGField[]): Promise<number> {
  const res = await upsertFields(clientId, fields);
  const ids = (res.entries ?? []).map((e) => e.id).filter(Boolean);
  if (ids.length) {
    // A failed verify leaves the row written but un-approved (verified=false). Surface it
    // so callers don't report "Saved" on a half-applied approval. Retry is idempotent:
    // upsert overwrites by field_path, verify by id.
    const { failed } = await batchVerifyEntries(clientId, ids);
    if (failed) throw new Error(`${failed} of ${ids.length} DNA item(s) could not be verified`);
  }
  // The approved rows change the client's DNA — drop the shared 5-min cache at
  // the write site so every DNA surface (LifeEvents widget, DnaCard, Client360)
  // refetches, instead of showing the pre-approval state until staleTime expires.
  void queryClient.invalidateQueries({ queryKey: ['client-dna', clientId] });
  return ids.length;
}

export async function verifyEntry(clientId: string, entryId: string) {
  return httpClient.put(`/api/knowledge-graph/${clientId}/verify/${entryId}`);
}

// ── Failed life-event → action recovery (TASK-576 finding F3) ──────────────
// A dropped mandatory compliance action (SoW/CRS/suitability) surfaces here so the
// RM can Retry it, instead of the action being silently lost. Resolves automatically
// once a retry creates the task (backend derives "unresolved" via LEFT JOIN).

export interface FailedLifeEventAction {
  event_id: string;
  client_id: string | null;
  task_type: string;
  event_type: string | null;
  error: string | null;
  failed_at: string;
}

export async function getFailedLifeEventActions(): Promise<FailedLifeEventAction[]> {
  const res = await httpClient.get<{ total: number; failures: FailedLifeEventAction[] }>(
    '/api/knowledge-graph/life-event-actions/failed'
  );
  return res.failures ?? [];
}

export async function retryFailedLifeEventAction(eventId: string) {
  return httpClient.post<{ event_id: string; retried: boolean; created_task_ids: string[] }>(
    `/api/knowledge-graph/life-event-actions/failed/${eventId}/retry`
  );
}

/**
 * Verify multiple KG entries in parallel.
 * Uses Promise.allSettled to handle partial failures gracefully.
 */
export async function batchVerifyEntries(
  clientId: string,
  entryIds: string[]
): Promise<{ succeeded: number; failed: number }> {
  const results = await Promise.allSettled(
    entryIds.map((id) => verifyEntry(clientId, id))
  );
  const succeeded = results.filter((r) => r.status === 'fulfilled').length;
  return { succeeded, failed: results.length - succeeded };
}
